import ComposeComponentProps from './ComposeComponentProps';
import ComposeDarkModeToggle from './ComposeDarkModeToggle';
import ComposeDropHint from './ComposeDropHint';
import ComposePreferredEditorToggle from './ComposePreferredEditorToggle';
import logEditorPerf from '../utils/logEditorPerf';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { logUsage } from 'owa-analytics';
import { AttachmentFullViewState } from 'owa-attachment-full-data';
import { AttachmentWellView } from 'owa-attachment-well-view';
import { LazyComponent } from 'owa-bundling';
import { ClientItemId } from 'owa-client-ids';
import { ControlIcons } from 'owa-control-icons';
import Droppable from 'owa-dnd/lib/components/Droppable';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { LocalFilesDragData } from 'owa-dnd/lib/utils/LocalFilesDragData';
import { BareContentEditableDivEditor, IEditor, ReactEditor } from 'owa-editor';
import getMentionClassName from 'owa-editor-mentions-picker-plugin/lib/utils/getMentionClassName';
import { PasteOption } from 'owa-editor-paste-option-plugin';
import { ContextualFormatBar } from 'owa-editor-ribbonplugin';
import { TableEditContextMenu } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { MailComposeSmartPillBlock } from 'owa-mail-compose-smart-pill';
import { lazyCreateAttachments } from 'owa-mail-compose-store';
import isComposeDarkModeEnabled from 'owa-mail-compose-store/lib/utils/isComposeDarkModeEnabled';
import setExplicitTextDirection from 'owa-mail-compose-store/lib/actions/setExplicitTextDirection';
import setIsFocusWorkaroundDivShown from 'owa-mail-compose-store/lib/actions/setIsFocusWorkaroundDivShown';
import triggerPolicyTips from 'owa-mail-compose-store/lib/actions/triggerPolicyTips';
import updateLastFocusedControl from 'owa-mail-compose-store/lib/actions/updateLastFocusedControl';
import { getPluginArray } from 'owa-mail-compose-store/lib/plugins/ComposePlugins';
import TextDirection from 'owa-mail-compose-store/lib/store/schema/TextDirection';
import { addViewStateToBeforeUnloadHandler } from 'owa-mail-compose-store/lib/utils/beforeUnloadHandler';
import { ComposeControl } from 'owa-mail-compose-store/lib/utils/getAutoFocusControl';
import upConvertHelper from 'owa-mail-compose-view/lib/utils/onUpconvertHelper';
import { getCurrentTableMailboxInfo } from 'owa-mail-mailboxinfo';
import { ComposeEditorType, getActiveEditorType } from 'owa-mail-preferred-editor';
import { mailStore } from 'owa-mail-store';
import { MailListItemPartDragData } from 'owa-mail-types/lib/types/MailListItemPartDragData';
import { MailListRowDragData } from 'owa-mail-types/lib/types/MailListRowDragData';
import { isPolicyTipsEnabled } from 'owa-policy-tips/lib/utils/isPolicyTipsEnabled';
import EventTrigger from 'owa-service/lib/contract/EventTrigger';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import strings from 'owa-strings/owa-mail-strings';
import { DarkModeContext } from 'owa-theme';
import { isBrowserEdge, isBrowserIE } from 'owa-user-agent/lib/userAgent';
import * as React from 'react';
import {
    AttachmentDragData,
    CreateAttachmentsProperties,
    lazyConvertImageViewAttachmentToInlineAttachment,
} from 'owa-attachment-well-data';
import {
    AttachmentFileType,
    createLocalComputerFiles,
    createMailItemFiles,
} from 'owa-attachment-file-types';
import {
    infoBarAddHandler,
    infoBarRemoveHandler,
} from 'owa-mail-compose-store/lib/utils/infoBarHandlers';

const styles = require('./ComposeBody.scss');
const classNames = require('classnames/bind').bind(styles);

type LazyEditor = LazyComponent<any, IEditor<any, any>>;

@observer
export default class ComposeBody extends React.Component<ComposeComponentProps, {}> {
    editorRef: LazyEditor;
    composeWrapperDivRef: React.RefObject<Droppable> = React.createRef();
    static contextType = DarkModeContext;

    componentDidMount() {
        addViewStateToBeforeUnloadHandler(this.props.viewState);
        this.setComposeWrapperOnRoosterPlugins();
    }

    componentWillUnmount() {
        this.editorRef = null;
    }

    componentDidUpdate() {
        // On update, if we are switching to a rooster editor, the plugins will have just been initialzied.
        // In that case, we need to inform the editor of the wrapper div it's sitting in
        // (for things like inline image plugin).
        const activeEditor: ComposeEditorType = getActiveEditorType(this.props.viewState);
        if (activeEditor === ComposeEditorType.RoosterHTML) {
            this.setComposeWrapperOnRoosterPlugins();
        }
    }

    public trySetFocus = () => {
        this.props.plugins.contentOperationPlugin.focusToEditor();
    };

    private getEditorContentFromDom = (): string => {
        const editor = this.editorRef && this.editorRef.getInnerComponent();
        if (!editor) {
            return null;
        }
        return editor.getContent();
    };

    render() {
        const { viewState } = this.props;
        const isDarkMode = this.context;
        const attachmentWell = viewState.attachmentWell;
        const showToggleContainer = isFeatureEnabled('cmp-downgradeRawCED');
        const hasAttachment =
            (attachmentWell.docViewAttachments && attachmentWell.docViewAttachments.length > 0) ||
            (attachmentWell.imageViewAttachments && attachmentWell.imageViewAttachments.length > 0);
        const bodyClassNames = classNames(styles.bodyContainer, {
            hasAttachment: hasAttachment,
            isInlineCompose: viewState.isInlineCompose,
            sticky: viewState.docking && viewState.docking.useStickyDocking,
            darkMode: isDarkMode,
        });
        return (
            <Droppable
                classNames={bodyClassNames}
                ref={this.composeWrapperDivRef}
                dropViewState={viewState.dropViewState}
                onDrop={this.onDrop}
                canDrop={this.canDrop}
                shouldUseCopyAsDropEffect={true}
                shouldIgnoreTransientOnDragLeave={true}>
                {this.renderAttachmentWell()}
                {this.renderFocusWorkaroundDiv()}
                {showToggleContainer && (
                    <div className={styles.toggleContainer}>
                        {isFeatureEnabled('cmp-downgradeRawCED') && (
                            <ComposePreferredEditorToggle
                                viewState={viewState}
                                getEditorContentFromDom={this.getEditorContentFromDom}
                            />
                        )}
                    </div>
                )}
                {this.renderEditor(isDarkMode)}
                {this.renderFocusWorkaroundDiv()}
                {isFeatureEnabled('mc-smartPillsCompose') && this.renderSmartPillBar()}
                {this.renderContextualFormatBar()}
                {this.renderEditTableMenu()}
                {this.renderPasteOption()}
                {this.renderShowHistoryBar()}
                {this.renderDropHint()}
                {isComposeDarkModeEnabled() && isFeatureEnabled('cmp-darkModeToggle') && (
                    <div
                        className={classNames(styles.darkModeToggleButton, {
                            darkMode: isDarkMode,
                        })}>
                        <ComposeDarkModeToggle viewState={viewState} />
                    </div>
                )}
            </Droppable>
        );
    }

    private setComposeWrapperOnRoosterPlugins() {
        const { plugins } = this.props;
        const div = this.composeWrapperDivRef.current
            ? this.composeWrapperDivRef.current.getDiv()
            : null;
        if (plugins && plugins.dockingPlugin) {
            plugins.dockingPlugin.setDockingTriggerPart(div);
        }

        if (plugins.inlineImageResizePlugin) {
            plugins.inlineImageResizePlugin.addOnImportedCallback(plugin =>
                plugin.setComposeBodyWrapperDiv(div)
            );
        }
    }

    private renderDropHint(): JSX.Element {
        return (
            this.shouldShowComposeDropHint() &&
            this.props.plugins && (
                <ComposeDropHint
                    dataTransferType={mailStore.typeOfItemBeingDragged}
                    dropViewState={this.props.viewState.dropViewState}
                    dockingPlugin={this.props.plugins.dockingPlugin}
                />
            )
        );
    }

    private shouldShowComposeDropHint = (): boolean => {
        // If classic attachments are disabled then we disable drag and drop
        if (!this.classicAttachmentsEnabled()) {
            return false;
        }

        // Compose body only allows to drop MailListRow and LocalFile.
        return (
            isFeatureEnabled('doc-attachment-composeDropHint') &&
            (mailStore.typeOfItemBeingDragged === DraggableItemTypes.MailListRow ||
                mailStore.typeOfItemBeingDragged ===
                    DraggableItemTypes.MultiMailListConversationRows ||
                mailStore.typeOfItemBeingDragged === DraggableItemTypes.MultiMailListMessageRows ||
                mailStore.typeOfItemBeingDragged === DraggableItemTypes.LocalFile ||
                mailStore.typeOfItemBeingDragged === DraggableItemTypes.Attachment)
        );
    };

    private onAttachmentDeleted = () => {
        if (isPolicyTipsEnabled()) {
            // When attachment is deleted, check DLP information.
            triggerPolicyTips(this.props.viewState, EventTrigger.AttachmentRemoved);
        }
    };

    private onAttachmentMovedInline = (attachment: AttachmentFullViewState, contentId: string) => {
        lazyConvertImageViewAttachmentToInlineAttachment.importAndExecute(
            this.props.viewState.attachmentWell,
            attachment,
            contentId
        );
    };

    private renderAttachmentWell(): JSX.Element {
        const { viewState } = this.props;
        const parentItemId: ClientItemId = {
            ...viewState.itemId,
            mailboxInfo: getCurrentTableMailboxInfo(),
        };
        return (
            <AttachmentWellView
                attachmentWellViewState={viewState.attachmentWell}
                parentItemId={parentItemId}
                isDraft={true}
                infoBarAddHandler={infoBarAddHandler(this.props.viewState)}
                infoBarRemoveHandler={infoBarRemoveHandler(this.props.viewState)}
                onAttachmentDeletedCallback={this.onAttachmentDeleted}
                moveAttachmentToInlineHandler={
                    viewState.bodyType == 'HTML' ? this.onAttachmentMovedInline : null
                }
            />
        );
    }

    private isRtl() {
        return this.props.viewState.explicitTextDirection
            ? this.props.viewState.explicitTextDirection === TextDirection.RTL
            : getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;
    }

    private renderEditor(isDarkMode: boolean): JSX.Element {
        const { viewState, plugins } = this.props;
        const isHtmlEditor = this.props.viewState.bodyType == 'HTML';
        const editorClassNames = classNames(
            styles.editorBody,
            viewState.subjectPickerViewState.isBackButtonHovered && styles.fadedOut,
            {
                isInlineCompose: viewState.isInlineCompose,
                hasRibbon: viewState.isRibbonShown,
                plainTextEditor: !isHtmlEditor,
                darkMode: isDarkMode,
            }
        );

        // TODO: VSO 1311: Temporarily keep TextArea for now. We will finally implement a PlainText editor using CED and remove TextArea
        const activeEditor: ComposeEditorType = getActiveEditorType(viewState);
        switch (activeEditor) {
            case ComposeEditorType.RoosterHTML:
                return (
                    <ReactEditor
                        plugins={getPluginArray(plugins)}
                        undo={plugins.undo}
                        viewState={viewState}
                        classNames={editorClassNames}
                        onFocus={this.onEditorFocus}
                        onBlur={this.onEditorBlur}
                        onDirectionChange={this.onEditorDirectionChange}
                        showFormatPainterCursor={
                            !!viewState.ribbon.formatViewState.formatPainterFormat
                        }
                        isRtl={this.isRtl()}
                        cssClassesToPreserveWhenPaste={[getMentionClassName()]}
                        logPerfDataCallback={
                            isFeatureEnabled('cmp-editorPerfMeasure') ? logEditorPerf : null
                        }
                        ariaLabel={strings.messageBodyAriaLabel}
                        isDarkMode={isDarkMode}
                    />
                );
            case ComposeEditorType.RawContentEditableDivHTML:
                return (
                    <BareContentEditableDivEditor
                        // These types are incompatible so we must cast up to 'any' and back down
                        ref={component => (this.editorRef = (component as any) as LazyEditor)}
                        className={editorClassNames}
                        viewState={viewState}
                    />
                );
            case ComposeEditorType.Textarea:
                return (
                    <div>
                        <textarea
                            className={editorClassNames}
                            placeholder={strings.addMessage}
                            ref={(ref: HTMLTextAreaElement) => {
                                if (this.props.plugins.contentOperationPlugin) {
                                    this.props.plugins.contentOperationPlugin.setPlainTextEditor(
                                        ref
                                    );
                                }
                                if (this.props.plugins.addinPlugin) {
                                    this.props.plugins.addinPlugin.setPlainTextEditor(ref);
                                }
                            }}
                            onFocus={this.onEditorFocus}
                            onChange={this.onPlainTextChange}
                            value={viewState.content}
                        />
                    </div>
                );
        }
    }

    private renderContextualFormatBar(): JSX.Element {
        return (
            <ContextualFormatBar
                className={styles.contextualFormatBar}
                ribbonPlugin={this.props.plugins.ribbonPlugin}
                viewState={this.props.viewState.ribbon}
                isRtl={this.isRtl()}
            />
        );
    }

    private renderPasteOption(): JSX.Element {
        const { pasteOptionPlugin } = this.props.plugins;
        return (
            pasteOptionPlugin && (
                <PasteOption
                    viewState={this.props.viewState.pasteOption}
                    plugin={pasteOptionPlugin}
                />
            )
        );
    }

    private renderShowHistoryBar(): JSX.Element {
        const { viewState } = this.props;
        return (
            viewState.isInlineCompose && (
                <IconButton
                    title={strings.showMessageHistory}
                    ariaLabel={strings.showMessageHistory}
                    onClick={this.onUpConvert}
                    className={styles.showHistoryButton}
                    iconProps={{
                        iconName: ControlIcons.More,
                        styles: {
                            root: styles.showHistoryButtonIcon,
                        },
                    }}
                />
            )
        );
    }

    private renderEditTableMenu(): JSX.Element {
        return (
            <TableEditContextMenu
                viewState={this.props.viewState.tableEdit}
                plugin={this.props.plugins.tableEdit}
            />
        );
    }

    private renderSmartPillBar(): JSX.Element {
        return <MailComposeSmartPillBlock />;
    }

    private onPlainTextChange = () => {
        this.props.plugins.contentOperationPlugin.updateContentToViewState();
    };

    private onUpConvert = async () => {
        upConvertHelper(this.props.viewState);
    };

    private onDrop = async (dragInfo: DragData) => {
        let attachmentFileList = null;
        let shouldShare = false;
        switch (dragInfo.itemType) {
            case DraggableItemTypes.LocalFile:
                const filesDragData = dragInfo as LocalFilesDragData;
                attachmentFileList = createLocalComputerFiles(filesDragData.files);
                break;
            case DraggableItemTypes.MailListRow:
            case DraggableItemTypes.MultiMailListConversationRows:
            case DraggableItemTypes.MultiMailListMessageRows:
                const mailListItemsDragData = dragInfo as MailListRowDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemsDragData.subjects,
                    mailListItemsDragData.latestItemIds
                );
                break;
            case DraggableItemTypes.MailListItemPart:
                const mailListItemPartDragData = dragInfo as MailListItemPartDragData;
                attachmentFileList = createMailItemFiles(
                    mailListItemPartDragData.subjects,
                    mailListItemPartDragData.itemIds
                );
                break;
            case DraggableItemTypes.Attachment:
                const attachmentDragData = dragInfo as AttachmentDragData;
                attachmentFileList = [attachmentDragData.attachmentFile];
                shouldShare =
                    attachmentDragData.attachmentFile.fileType === AttachmentFileType.Cloud ||
                    attachmentDragData.attachmentFile.fileType ===
                        AttachmentFileType.CloudSuggestion;
                break;
            default:
                break;
        }

        if (attachmentFileList) {
            logUsage('AttachmentDragAndDrop', [dragInfo.itemType]);
            const createAttachments = await lazyCreateAttachments.import();
            const baseProperties = { isInline: false, isHiddenInline: false };
            const properties: CreateAttachmentsProperties = shouldShare
                ? { ...baseProperties, shouldShare: true }
                : { ...baseProperties, shouldShare: false };
            createAttachments(attachmentFileList, this.props.viewState, properties, null);
        }
    };

    private canDrop = (dragInfo: DragData) => {
        // If classic attachments are disabled then we disable drag and drop
        if (!this.classicAttachmentsEnabled()) {
            return false;
        }

        const itemType = dragInfo.itemType;
        return (
            itemType === DraggableItemTypes.LocalFile ||
            itemType === DraggableItemTypes.MailListItemPart ||
            itemType === DraggableItemTypes.MailListRow ||
            itemType === DraggableItemTypes.MultiMailListConversationRows ||
            itemType === DraggableItemTypes.MultiMailListMessageRows ||
            itemType === DraggableItemTypes.Attachment
        );
    };

    // Workaround a behavior in Edge/IE
    // Add an extra DIV before and after editor to accept the focus by press TAB/Shift+Tab then use code to trigger the real focus into editor
    // So that the view will not do auto scroll
    private renderFocusWorkaroundDiv(): JSX.Element {
        return (
            (isBrowserEdge() || isBrowserIE()) &&
            this.props.viewState.isFocusWorkaroundDivShown && (
                <div onFocus={this.trySetFocus} tabIndex={0} />
            )
        );
    }

    private onEditorFocus = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, false /*isShown*/);
        updateLastFocusedControl(this.props.viewState, ComposeControl.Editor);
    };

    private onEditorBlur = () => {
        setIsFocusWorkaroundDivShown(this.props.viewState, true /*isShown*/);
    };

    private onEditorDirectionChange = (editorDirection: 'ltr' | 'rtl') => {
        if (this.props.viewState.explicitTextDirection !== editorDirection) {
            setExplicitTextDirection(this.props.viewState, editorDirection as TextDirection);
        }
    };

    private classicAttachmentsEnabled = () => {
        const userConfiguration = getUserConfiguration();
        return userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled;
    };
}
